<script setup lang="ts">
import { watch } from 'vue'

import { useCustomizeStore } from '../../stores/customizeStore'

import { reportTabIcon } from './reportTabIcons'

import {
  getAccountDetailPackageId,
  isFreePlanLockedReportTab,
  isFreePlanPackage,
  useAccount,
} from '@/features/account'
import { requestPlanSelection } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui/icon'

// The page tab bar (v3 OrphexReportTabs + OrphexTabs merged, ORP-18652). Panels belong to the
// page host, so this is a bare tablist; selection persists per page key via customizeStore so
// the active tab survives the workspace-keyed remount.

const TAB_KIND = 'reportPageTabV1'

const props = defineProps<{
  /** Persistence partition (the page's layout key). Omit for non-persistent tab bars. */
  id?: string
  tabs: readonly {
    name: string
    labelKey: string
    beta?: boolean
    soon?: boolean
    image?: string
  }[]
  ariaLabel: string
}>()
const model = defineModel<string | null>({ default: null })
const { t } = useI18n()
const cache = useCustomizeStore()
const account = useAccount()

function isPlanLocked(name: string): boolean {
  return (
    isFreePlanPackage(getAccountDetailPackageId(account.accountDetail.value)) &&
    isFreePlanLockedReportTab(props.id, name)
  )
}

function parseSaved(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null
  const tab = (value as Record<string, unknown>).tab
  return typeof tab === 'string' ? tab : null
}

// Restore then validate: an invalid/absent selection snaps to the first tab (v3 :139-150).
watch(
  () => [props.id, props.tabs] as const,
  ([id, tabs]) => {
    const saved = id ? parseSaved(cache.getCached<unknown>(id, TAB_KIND)) : null
    const current = model.value
    const valid = (name: string | null): boolean => tabs.some((tab) => tab.name === name)
    model.value = valid(current) ? current : valid(saved) ? saved : (tabs[0]?.name ?? null)
  },
  { immediate: true },
)

function select(name: string): void {
  if (props.tabs.find((tab) => tab.name === name)?.soon) return
  if (isPlanLocked(name)) {
    requestPlanSelection()
    return
  }
  model.value = name
  if (props.id) cache.setCached(props.id, TAB_KIND, { tab: name })
}
</script>

<template>
  <div
    v-if="tabs.length > 1"
    class="report-tabs flex overflow-x-auto items-center gap-xs"
    role="tablist"
    :aria-label="ariaLabel"
  >
    <button
      v-for="tab in tabs"
      :key="tab.name"
      type="button"
      role="tab"
      class="report-tabs__tab relative inline-flex items-center gap-xs min-w-24 h-11 overflow-hidden py-0 px-sm border-0 rounded-ss-sm rounded-se-sm bg-[color-mix(in_srgb,_var(--ui-color-surface)_60%,_transparent)] text-primary-soft-text cursor-pointer text-[0.875rem] whitespace-nowrap"
      :class="{ 'report-tabs__tab--active': model === tab.name, 'pointer-events-none': tab.soon }"
      :aria-selected="model === tab.name"
      :aria-disabled="isPlanLocked(tab.name) || tab.soon"
      :tabindex="model === tab.name ? 0 : -1"
      @click="select(tab.name)"
    >
      <img v-if="tab.image" class="report-tabs__image" :src="tab.image" alt="" />
      <Icon
        v-else-if="reportTabIcon(tab.name)"
        class="report-tabs__icon"
        :name="reportTabIcon(tab.name)!.name"
        :weight="reportTabIcon(tab.name)!.weight"
        :size="reportTabIcon(tab.name)!.size ?? 16"
      />
      {{ t(tab.labelKey) }}
      <span
        v-if="tab.soon"
        class="report-tabs__pill report-tabs__pill--soon py-0.5 px-2 rounded-sm bg-[var(--ui-color-tab-soon-bg)] text-xs"
        >{{ t('reports.tabs.soon') }}</span
      >
      <span
        v-else-if="tab.beta"
        class="report-tabs__pill report-tabs__pill--beta ms-1 py-0.5 px-1.5 rounded-sm bg-[var(--ui-color-tab-beta-bg)] text-[var(--ui-color-tab-beta-fg)] text-[0.625rem] leading-3 font-bold uppercase"
        >{{ t('reports.tabs.beta') }}</span
      >
      <Icon v-if="isPlanLocked(tab.name)" class="ms-1" name="lock" weight="light" :size="12" />
    </button>
  </div>
</template>

<style scoped>
.report-tabs .report-tabs__image {
  width: 1rem;
  height: 1rem;
  object-fit: contain;
}
.report-tabs .report-tabs__tab--active {
  color: var(--ui-color-primary);
  background-color: var(--ui-color-surface);
  font-weight: 600;
}
.report-tabs .report-tabs__tab--active .report-tabs__icon {
  color: var(--ui-color-accent);
}
.report-tabs .report-tabs__tab--active::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  background-color: var(--ui-color-tab-accent);
  content: '';
}
</style>
